import type {
  FilaHistorial,
  GrupoLinea,
  HistorialPuerta,
  ParteMarcado,
  ParteReplicaRow,
  RevisionReplicaRow,
} from '@/types/clientes';

// Marca los partes que tocaron la puerta de la llamada. MARCA, no filtra, y esa
// es toda la gracia (decisión D2, docs/odoo-project-task-2026-08-21.md).
//
// El equipo de un parte es un many2many y el 65% de los partes reales no cuelga
// de ninguna puerta. Filtrando por equipo se enseñaría el 35% del historial, y
// el cliente echaría en falta visitas que recuerda perfectamente. Así el panel
// —y el bot— pueden decir "de esa puerta tengo dos, y cinco más suyas en total"
// sin dejarse nada fuera ni inventarse una relación que no existe.
//
// Sin equipo identificado no se marca ninguno: no es un error, es la mitad de
// las llamadas.
export function marcarPartesDeLaPuerta(
  partes: ParteReplicaRow[],
  idEquipo: string | null
): ParteMarcado[] {
  return partes.map((p) => ({
    ...p,
    deLaPuerta: idEquipo != null && p.ids_equipos.includes(idEquipo),
  }));
}

// ---------------------------------------------------------------------------
// El historial DE LA PUERTA: las dos mitades entrelazadas por fecha
// ---------------------------------------------------------------------------
// Change 001 del historial. La spec base decidió marcar sin filtrar (D2) porque
// dos tercios de los partes no llevan puerta, y ese argumento sigue en pie: es
// la razón de que el resto del cliente se pliegue en vez de desaparecer. Lo que
// cambió es que entró una fuente que SÍ lleva puerta siempre —las revisiones—,
// así que la vista de una puerta deja de ser un subconjunto pobre (D10).
//
// No se pueden fusionar por parte: solo el 34% de las revisiones lleva
// `tarea_id` y leerlo revienta en este build de Odoo. Lo único que las dos
// tienen siempre es la fecha.

// Las revisiones que no dicen nada se OCULTAN y se CUENTAN (D9). El
// significado importa: en una revisión de mantenimiento «Sin observaciones» es
// «la miramos y estaba bien», que es justo lo que hace falta contestar cuando
// preguntan si han pasado por allí. Se esconde la fila muda, no el hecho.
//
// Y la comparación va NORMALIZADA, no contra un literal. Medido en producción el
// 9-sep sobre las 18.741 filas: `Sin Observaciones` con O mayúscula son 6.187 y
// `Sin observaciones` 1.859. Comparar con una sola de las dos dejaría pasar el
// 77% de las mudas — y hay una fila que lleva la frase como SUFIJO
// («Sin junta intumescente. Sin Observaciones») que tiene contenido real y tiene
// que verse, así que la comparación es de igualdad, no de `includes`.
const MUDA = 'sin observaciones';

function esMuda(observaciones: string | null): boolean {
  if (observaciones == null) return true;
  const normal = observaciones.replace(/\s+/g, ' ').trim().toLowerCase();
  return normal === '' || normal === MUDA;
}

// Orden: fecha DESC y las sin fecha AL FINAL.
//
// En Postgres un `ORDER BY ... DESC` pone los NULL primero, y eso fue un bug
// real de esta misma pantalla (hallazgo M3 del verify de historial-de-partes):
// un parte sin fecha encabezaba el historial haciéndose pasar por el más
// reciente. Aquí el orden es en memoria, pero el defecto sería idéntico, así que
// se escribe explícito. Hoy no hay ninguna fila sin fecha (0 de 18.741
// revisiones y 0 de 4.624 partes): es la red para el día que Odoo mande una.
//
// A igualdad de fecha, la revisión va encima: el técnico anota DESPUÉS de que el
// cliente abra el parte. Y el último desempate es por id, para que el orden no
// dependa del orden de entrada.
function comparar(a: FilaHistorial, b: FilaHistorial): number {
  const ta = a.fecha ? Date.parse(a.fecha) : NaN;
  const tb = b.fecha ? Date.parse(b.fecha) : NaN;
  const va = Number.isNaN(ta);
  const vb = Number.isNaN(tb);
  if (va !== vb) return va ? 1 : -1;
  if (!va && ta !== tb) return tb - ta;
  if (a.tipo !== b.tipo) return a.tipo === 'revision' ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

const deParte = (p: ParteReplicaRow): FilaHistorial => ({
  id: p.id,
  tipo: 'parte',
  quien: 'cliente',
  fecha: p.fecha,
  texto: p.descripcion,
  titulo: p.titulo,
  linea_negocio: p.linea_negocio,
  etiqueta_estado: p.etapa,
  referencia: p.id_odoo,
});

const deRevision = (r: RevisionReplicaRow): FilaHistorial => ({
  id: r.id,
  tipo: 'revision',
  quien: 'tecnico',
  fecha: r.fecha,
  texto: r.observaciones,
  titulo: null,
  linea_negocio: r.linea_negocio,
  etiqueta_estado: r.estado,
  referencia: null,
});

// El mantenimiento del contrato NO compite por sitio con lo que le pasó a la
// puerta (change 002, D11-D14). Medido en producción: de los 10 partes de PUERTA
// GUILLOTINA ENTRADA, NUEVE son `Mantenimiento CTO MTO ANUAL - <fecha>` creados en
// tandas de cuatro, y lo único que le pasó de verdad a esa puerta —una reparación—
// quedaba en la fila 5, debajo de la planificación. Es la petición literal de
// Manuel del 3-sep.
//
// El discriminador es `linea_negocio` y no `etapa` ni el título (D11): `etapa`
// tiene diez valores y `PLANIFICADO` no significa futuro —105 de 158 ya pasaron—,
// y el título es texto libre. `linea_negocio` se sincronizó el 3-sep justo para
// discriminar esto.
//
// Normalizado aunque los siete valores de producción estén limpios (T1 del plan):
// es barato, y la lección de «Sin observaciones» es que donde parecía haber una
// forma había cuatro. Igualdad, no `includes`: «MANTENIMIENTO CORRECTIVO» es una
// reparación con otro nombre y tiene que quedarse en el historial.
const LINEA_MANTENIMIENTO = 'mantenimiento';
const ETAPA_PLANIFICADO = 'planificado';

const normal = (v: string | null): string => (v ?? '').replace(/\s+/g, ' ').trim().toLowerCase();

// Exportada porque el panel la necesita para la MISMA decisión: un parte de
// mantenimiento de la puerta sale del entrelazado pero tiene que seguir estando
// en el resto del cliente. Con dos criterios distintos —uno aquí y otro en el
// componente— desaparecía de la pantalla entera.
export function esMantenimientoDeContrato(parte: { linea_negocio: string | null }): boolean {
  return normal(parte.linea_negocio) === LINEA_MANTENIMIENTO;
}

export function entrelazar(
  partesDeLaPuerta: ParteReplicaRow[],
  revisiones: RevisionReplicaRow[]
): HistorialPuerta {
  // Las REVISIONES no se reparten por línea, aunque el 84% sean de mantenimiento
  // (15.757 de 18.741): un parte de mantenimiento es la PROGRAMACIÓN de una visita
  // y la revisión es lo que el técnico ENCONTRÓ en ella. Se filtra la
  // programación, no el contenido (D13). Y desde que las revisiones están dentro,
  // el parte de mantenimiento aporta una fecha y nada más mientras la revisión de
  // esa misma visita aporta el texto: tenerlos compitiendo era duplicar el hecho
  // quedándose con la mitad muda.
  const conVoz = revisiones.filter((r) => !esMuda(r.observaciones));
  const mantenimiento = partesDeLaPuerta.filter(esMantenimientoDeContrato);
  const delHistorial = partesDeLaPuerta.filter((p) => !esMantenimientoDeContrato(p));
  const filas = [...delHistorial.map(deParte), ...conVoz.map(deRevision)].sort(comparar);
  return {
    filas,
    revisionesOcultas: revisiones.length - conVoz.length,
    totalRevisiones: revisiones.length,
    // Los de la puerta, mantenimiento incluido: es lo que dice la cabecera y no
    // cambia por cómo se repartan las filas.
    totalPartes: partesDeLaPuerta.length,
    mantenimiento: {
      total: mantenimiento.length,
      // La etapa, no una fecha: «PLANIFICADO» es lo que dice Odoo y no significa
      // «futuro» —105 de 158 ya pasaron— pero sirve para no dar por hecha una
      // visita que quizá no ocurrió. La fecha más reciente se quitó: era el día en
      // que se creó la tanda de planificación, no una visita.
      planificados: mantenimiento.filter((p) => normal(p.etapa) === ETAPA_PLANIFICADO).length,
    },
  };
}

// ---------------------------------------------------------------------------
// El historial SIN puerta: agrupado por línea de negocio
// ---------------------------------------------------------------------------
// El otro modo (D7). Manuel pidió el 3-sep separar las reparaciones de las
// inspecciones, y no lo pidió condicionado a identificar la puerta: sin puerta
// no hay eje que valga, así que el eje pasa a ser la línea.
//
// El grupo de los NULL es obligatorio, no un adorno defensivo: la línea sale de
// `project.type` y hay partes que en Odoo no cuelgan de ningún proyecto. Hoy es
// UNO de 4.624 (el parte 5108, un montaje de prueba), y sin grupo desaparecería
// de la pantalla sin que nada fallase. Y si esta pantalla llegara a producción
// antes de un `full`, serían TODOS.
const SIN_CLASIFICAR = 'Sin clasificar';

// Vacío o solo espacios cuenta como sin clasificar: un grupo cuyo título es la
// cadena vacía es peor que no tenerlo.
const clave = (linea: string | null): string | null => {
  const l = (linea ?? '').trim();
  return l === '' ? null : l;
};

export function agruparPorLinea(partes: ParteReplicaRow[]): GrupoLinea[] {
  // `Map` y no un objeto: conserva el orden de inserción, que dentro de cada
  // grupo es el de entrada — y la entrada viene ya por fecha DESC del servicio.
  const grupos = new Map<string | null, ParteReplicaRow[]>();
  for (const p of partes) {
    const k = clave(p.linea_negocio);
    const lista = grupos.get(k);
    if (lista) lista.push(p);
    else grupos.set(k, [p]);
  }

  return (
    [...grupos.entries()]
      .map(([linea, lista]) => ({
        linea,
        etiqueta: linea ?? SIN_CLASIFICAR,
        partes: lista,
      }))
      // Los de más volumen primero, y a igualdad alfabético para que el orden no
      // dependa del de entrada. `Sin clasificar` va SIEMPRE al final aunque sea el
      // grupo más grande: encabezar la pantalla con «no sabemos de qué es» sería
      // enseñar el hueco antes que el dato.
      .sort((a, b) => {
        if ((a.linea === null) !== (b.linea === null)) return a.linea === null ? 1 : -1;
        if (a.partes.length !== b.partes.length) return b.partes.length - a.partes.length;
        return (a.linea ?? '').localeCompare(b.linea ?? '', 'es');
      })
  );
}

// ---------------------------------------------------------------------------
// Lo que dice el bloque de la puerta cuando no tiene NINGUNA fila de historial
// ---------------------------------------------------------------------------
// Función pura y aparte porque son cinco hechos que se combinan, y la versión
// anterior era una cadena de ternarios que ELEGÍA uno: cada combinación nueva
// mentía por un lado distinto —«no consta ningún parte» en una puerta con tres de
// mantenimiento, o un «solo» falso habiendo revisiones mudas, o comerse el aviso
// de que las revisiones no se pudieron leer—. Tres vueltas del gate cayeron en
// esta misma forma antes de que la sacara aquí.
//
// Cada trozo afirma UNA cosa que es verdad por sí sola, y se suman.
export function frasePuertaVacia(x: {
  fallo: boolean;
  falloRevisiones: boolean;
  mantenimientos: number;
  revisionesOcultas: number;
}): string {
  // CADA trozo habla solo de LO SUYO —los partes de los partes, las revisiones de
  // las revisiones— y ninguno hace una afirmación sobre «la puerta» entera. Esa es
  // la corrección de la segunda vuelta del gate: la primera versión de esta misma
  // función, escrita para cerrar la clase, seguía diciendo «SOLO consta
  // mantenimiento» al lado de «y sus revisiones no llevan observaciones» —o al
  // lado de «las revisiones no se han podido consultar»—, que es la contradicción
  // de antes con otra redacción. Quitando el ámbito «puerta» de las frases, la
  // palabra «solo» desaparece y no hay nada que contradecir.
  const partes = x.fallo
    ? 'el historial de partes no se ha podido consultar'
    : x.mantenimientos > 0
      ? 'los únicos partes de esta puerta son mantenimiento del contrato'
      : 'de esta puerta no consta ningún parte de avería ni mejora';

  const revisiones = x.falloRevisiones
    ? 'las revisiones no se han podido consultar'
    : x.revisionesOcultas > 0
      ? 'sus revisiones no llevan observaciones: se revisó y estaba bien'
      : null;

  // Y si de las dos mitades no hay NADA que matizar —los partes se leyeron y no hay
  // ninguno, las revisiones se leyeron y tampoco—, entonces sí se puede hablar de
  // la puerta: es el único caso en el que se sabe todo.
  if (!x.fallo && !x.falloRevisiones && x.mantenimientos === 0 && x.revisionesOcultas === 0) {
    return 'De esta puerta no consta ninguna intervención todavía.';
  }

  const frase = revisiones == null ? partes : `${partes}, y ${revisiones}`;
  return `${frase.charAt(0).toUpperCase()}${frase.slice(1)}.`;
}
